import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { LoginPage } from '@/features/auth/LoginPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { MfaPage } from '@/features/auth/MfaPage';
import { LibraryPage } from '@/features/library/LibraryPage';
import { PresetPage } from '@/features/library/PresetPage';
import { PlansPage } from '@/features/plans/PlansPage';
import { SettingsRoute } from '@/features/settings/SettingsPage';
import { AuditRoute } from '@/features/audit/AuditPage';
import { OrganizationLayout, PendingOrgTab } from '@/features/organizations/OrganizationPage';
import { OrganizationsPage } from '@/features/organizations/OrganizationsPage';
import { ActivityTab } from '@/features/organizations/ActivityTab';
import { OverviewTab } from '@/features/organizations/OverviewTab';
import { SubscriptionTab } from '@/features/organizations/SubscriptionTab';
import { UsersTab } from '@/features/organizations/UsersTab';
import { IntegrationsTab } from '@/features/integrations/IntegrationsTab';
import { OrgResearchTab, SiteResearchTab } from '@/features/research/ResearchTabs';
import { OrgSitesTab } from '@/features/sites/OrgSitesTab';
import { PendingSiteTab, SiteIndexRedirect, SiteLayout } from '@/features/sites/SitePage';
import { OverviewTab as SiteOverview } from '@/features/sites/OverviewTab';
import { SitesPage } from '@/features/sites/SitesPage';
import { StructureTab } from '@/features/structure/StructureTab';
import { VariantsTab } from '@/features/variants/VariantsTab';
import { DesignerTab } from '@/features/designer/DesignerTab';
import { AnalyticsTab } from '@/features/analytics/AnalyticsTab';
import { DeployTab } from '@/features/deploy/DeployTab';
import { DomainsTab } from '@/features/domains/DomainsTab';
import { StoreSyncTab } from '@/features/store/StoreSyncTab';
import { AppShell } from '@/shell/AppShell';
import { env } from './env';
import { GlobalActionsProvider } from './GlobalActions';
import { RouteError } from './ErrorBoundary';
import { Placeholder } from './Placeholder';

// Dev-only routes: the design-system gallery and the token check page (split out of the main bundle).
const TokensPage = lazy(() => import('@/design-system/gallery/TokensPage').then((m) => ({ default: m.TokensPage })));
const GalleryPage = lazy(() => import('@/design-system/gallery/GalleryPage').then((m) => ({ default: m.GalleryPage })));
const ThumbsPage = lazy(() => import('@/design-system/gallery/ThumbsPage').then((m) => ({ default: m.ThumbsPage })));
const devRoutes: RouteObject[] = env.devRoutes
  ? [
      { path: '/__ds', element: <Suspense><GalleryPage /></Suspense>, errorElement: <RouteError /> },
      { path: '/__ds/tokens', element: <Suspense><TokensPage /></Suspense>, errorElement: <RouteError /> },
      { path: '/__ds/thumbs', element: <Suspense><ThumbsPage /></Suspense>, errorElement: <RouteError /> },
    ]
  : [];

// Every route of the design (DESIGN_SPEC §4). Screens replace their placeholders task by task.
export const appRoutes: RouteObject[] = [
  { index: true, element: <DashboardPage /> },
  { path: 'organizations', element: <OrganizationsPage /> },
  {
    path: 'organizations/:orgId',
    element: <OrganizationLayout />,
    children: [
      { index: true, element: <Navigate to="overview" replace /> },
      { path: 'overview', element: <OverviewTab /> },
      { path: 'research', element: <OrgResearchTab /> },
      { path: 'sites', element: <OrgSitesTab /> },
      { path: 'users', element: <UsersTab /> },
      { path: 'integrations', element: <IntegrationsTab /> },
      { path: 'subscription', element: <SubscriptionTab /> },
      { path: 'activity', element: <ActivityTab /> },
      { path: ':tab', element: <PendingOrgTab /> },
    ],
  },
  { path: 'sites', element: <SitesPage /> },
  { path: 'sites/:siteId', element: <SiteIndexRedirect /> },
  {
    path: 'sites/:siteId',
    element: <SiteLayout />,
    children: [
      { path: 'overview', element: <SiteOverview /> },
      { path: 'research', element: <SiteResearchTab /> },
      { path: 'structure', element: <StructureTab /> },
      { path: 'variants', element: <VariantsTab /> },
      { path: 'designer', element: <DesignerTab /> },
      { path: 'sync', element: <StoreSyncTab /> },
      { path: 'domains', element: <DomainsTab /> },
      { path: 'deploy', element: <DeployTab /> },
      { path: 'analytics', element: <AnalyticsTab /> },
      { path: ':tab', element: <PendingSiteTab /> },
    ],
  },
  { path: 'library', element: <LibraryPage /> },
  { path: 'library/:presetId', element: <PresetPage /> },
  { path: 'plans', element: <PlansPage /> },
  { path: 'settings', element: <Navigate to="ai" replace /> },
  { path: 'settings/:tab', element: <SettingsRoute /> },
  { path: 'audit', element: <AuditRoute /> },
  { path: '*', element: <Placeholder screen="not-found" /> },
];

export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <GlobalActionsProvider>
        <AppShell />
      </GlobalActionsProvider>
    ),
    errorElement: <RouteError />,
    children: appRoutes,
  },
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/login/mfa', element: <MfaPage />, errorElement: <RouteError /> },
  ...devRoutes,
];

export function createRouter() {
  return createBrowserRouter(routes);
}
